// src/components/maps/preview/Building3DViewer.jsx
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
  Room as RoomIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Layers as LayersIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Navigation as NavigationIcon,
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

// Node color based on type
const getNodeColor = (type) => {
  switch (type) {
    case 'stairs': return 0x9C27B0;
    case 'elevator': return 0x009688;
    case 'entrance': return 0x4CAF50;
    default: return 0x2196F3;
  }
};

// Navigation route colors
const ROUTE_COLOR = 0x00E676; // Bright green
const ROUTE_GLOW_COLOR = 0x69F0AE;
const STAIRS_ROUTE_COLOR = 0xAB47BC; // Purple
const ELEVATOR_ROUTE_COLOR = 0x26A69A; // Teal
const DESTINATION_COLOR = 0xFF5722; // Orange-red

// Create a tube geometry for the route path (more visible than lines)
const createRouteTube = (points, color, radius = 0.06) => {
  if (points.length < 2) return null;

  const curve = new THREE.CatmullRomCurve3(points);
  const geometry = new THREE.TubeGeometry(curve, points.length * 10, radius, 8, false);
  const material = new THREE.MeshStandardMaterial({
    color: color,
    emissive: color,
    emissiveIntensity: 0.4,
    roughness: 0.3,
    metalness: 0.1,
  });
  return new THREE.Mesh(geometry, material);
};

// Create animated dashed line for route
const createAnimatedRouteLine = (points, color) => {
  if (points.length < 2) return null;

  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineDashedMaterial({
    color: color,
    dashSize: 0.3,
    gapSize: 0.15,
    linewidth: 2,
  });
  const line = new THREE.Line(geometry, material);
  line.computeLineDistances();
  return line;
};

// Create vertical line for floor transitions (stairs/elevator)
const createVerticalTransitionLine = (type, x, fromY, z, toY) => {
  const group = new THREE.Group();
  const color = type === 'stairs' ? STAIRS_ROUTE_COLOR : ELEVATOR_ROUTE_COLOR;
  const height = Math.abs(toY - fromY);
  const minY = Math.min(fromY, toY);

  // Main vertical tube (thick glowing line)
  const points = [
    new THREE.Vector3(x, minY + 0.1, z),
    new THREE.Vector3(x, minY + height + 0.1, z),
  ];
  const curve = new THREE.CatmullRomCurve3(points);
  const tubeGeometry = new THREE.TubeGeometry(curve, 8, 0.08, 8, false);
  const tubeMaterial = new THREE.MeshStandardMaterial({
    color: color,
    emissive: color,
    emissiveIntensity: 0.6,
    roughness: 0.3,
    metalness: 0.1,
  });
  const tube = new THREE.Mesh(tubeGeometry, tubeMaterial);
  group.add(tube);

  // Animated dashed line overlay
  const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
  const lineMaterial = new THREE.LineDashedMaterial({
    color: 0xFFFFFF,
    dashSize: 0.2,
    gapSize: 0.1,
    linewidth: 2,
  });
  const dashedLine = new THREE.Line(lineGeometry, lineMaterial);
  dashedLine.computeLineDistances();
  dashedLine.userData.isRoutePath = true;
  group.add(dashedLine);

  // Top and bottom markers (spheres)
  const markerGeometry = new THREE.SphereGeometry(0.12, 16, 16);
  const markerMaterial = new THREE.MeshStandardMaterial({
    color: color,
    emissive: color,
    emissiveIntensity: 0.5,
  });

  const bottomMarker = new THREE.Mesh(markerGeometry, markerMaterial);
  bottomMarker.position.set(x, minY + 0.1, z);
  group.add(bottomMarker);

  const topMarker = new THREE.Mesh(markerGeometry, markerMaterial);
  topMarker.position.set(x, minY + height + 0.1, z);
  topMarker.userData.isPulsingMarker = true;
  group.add(topMarker);

  // Icon indicator (simple shape to indicate stairs vs elevator)
  if (type === 'stairs') {
    // Small stairs icon - 3 steps
    for (let i = 0; i < 3; i++) {
      const stepGeometry = new THREE.BoxGeometry(0.2, 0.04, 0.08);
      const stepMaterial = new THREE.MeshStandardMaterial({
        color: color,
        emissive: color,
        emissiveIntensity: 0.3,
      });
      const step = new THREE.Mesh(stepGeometry, stepMaterial);
      step.position.set(x + 0.2, minY + height * 0.3 + i * 0.15, z);
      group.add(step);
    }
  } else {
    // Elevator icon - small box
    const boxGeometry = new THREE.BoxGeometry(0.15, 0.2, 0.15);
    const boxMaterial = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.4,
    });
    const box = new THREE.Mesh(boxGeometry, boxMaterial);
    box.position.set(x + 0.2, minY + height * 0.5, z);
    box.userData.isElevatorCabin = true;
    group.add(box);
  }

  group.userData.elevatorHeight = height;
  return group;
};

// Create destination marker (pulsing beacon)
const createDestinationMarker = (x, y, z) => {
  const group = new THREE.Group();
  group.position.set(x, y, z);

  // Main pin
  const pinGeometry = new THREE.ConeGeometry(0.15, 0.4, 16);
  const pinMaterial = new THREE.MeshStandardMaterial({
    color: DESTINATION_COLOR,
    emissive: DESTINATION_COLOR,
    emissiveIntensity: 0.5,
  });
  const pin = new THREE.Mesh(pinGeometry, pinMaterial);
  pin.rotation.x = Math.PI;
  pin.position.y = 0.4;
  group.add(pin);

  // Glowing sphere on top
  const sphereGeometry = new THREE.SphereGeometry(0.12, 16, 16);
  const sphereMaterial = new THREE.MeshStandardMaterial({
    color: 0xFFFFFF,
    emissive: DESTINATION_COLOR,
    emissiveIntensity: 0.8,
  });
  const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
  sphere.position.y = 0.65;
  sphere.userData.isPulsingMarker = true;
  group.add(sphere);

  // Ground ring
  const ringGeometry = new THREE.RingGeometry(0.2, 0.3, 32);
  const ringMaterial = new THREE.MeshStandardMaterial({
    color: DESTINATION_COLOR,
    emissive: DESTINATION_COLOR,
    emissiveIntensity: 0.3,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.7,
  });
  const ring = new THREE.Mesh(ringGeometry, ringMaterial);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.01;
  ring.userData.isPulsingRing = true;
  group.add(ring);

  return group;
};

// Create start marker
const createStartMarker = (x, y, z) => {
  const group = new THREE.Group();
  group.position.set(x, y, z);

  // Person icon (simplified)
  const headGeometry = new THREE.SphereGeometry(0.08, 16, 16);
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: 0x2196F3,
    emissive: 0x2196F3,
    emissiveIntensity: 0.3,
  });
  const head = new THREE.Mesh(headGeometry, bodyMaterial);
  head.position.y = 0.35;
  group.add(head);

  const bodyGeometry = new THREE.CylinderGeometry(0.05, 0.08, 0.2, 16);
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.position.y = 0.2;
  group.add(body);

  return group;
};

// Main Building3DViewer component
const Building3DViewer = ({
  buildingData,
  onNodeSelect,
  selectedDestination,
  isMobile = false,
  // Navigation mode props
  navigationRoute = null,  // Array of route steps with floor info
  isNavigating = false,
  currentStepIndex = 0,
}) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animationFrameRef = useRef(null);
  const nodeMeshesRef = useRef(new Map());
  const routeObjectsRef = useRef([]);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const animationTimeRef = useRef(0);

  const [expandedFloors, setExpandedFloors] = useState(!isMobile);
  const [showFloorPanel, setShowFloorPanel] = useState(!isMobile && !isNavigating);
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);

  const floorSpacing = 3;
  const planeWidth = 10;

  // Process navigation route into floor-based segments
  const processedRoute = useMemo(() => {
    if (!navigationRoute || !navigationRoute.length || !buildingData?.floors) return null;

    const floorMap = new Map();
    buildingData.floors.forEach(f => floorMap.set(f.id, f));

    // Group steps by floor
    const segments = [];
    let currentSegment = null;

    navigationRoute.forEach((step, index) => {
      const floor = floorMap.get(step.floor_plan_id);
      if (!floor) return;

      if (!currentSegment || currentSegment.floorId !== step.floor_plan_id) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          floorId: step.floor_plan_id,
          floor,
          steps: [],
          isTransition: step.node_type === 'stairs' || step.node_type === 'elevator',
          transitionType: step.node_type,
        };
      }
      currentSegment.steps.push({ ...step, index });
    });
    if (currentSegment) {
      segments.push(currentSegment);
    }

    return {
      segments,
      startStep: navigationRoute[0],
      endStep: navigationRoute[navigationRoute.length - 1],
      totalSteps: navigationRoute.length,
    };
  }, [navigationRoute, buildingData]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current || !buildingData?.floors?.length) return;

    setLoading(true);

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(isNavigating ? 0x0a0a1a : 0x1a1a2e);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(15, 15, 15);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 5;
    controls.maxDistance = 50;
    controls.maxPolarAngle = Math.PI / 2.1;
    const centerY = (buildingData.floors.length * floorSpacing) / 2;
    controls.target.set(0, centerY, 0);
    controlsRef.current = controls;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, isNavigating ? 0.4 : 0.6);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight1.position.set(10, 20, 10);
    directionalLight1.castShadow = true;
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-10, 20, -10);
    scene.add(directionalLight2);

    // Ground plane
    const groundGeometry = new THREE.PlaneGeometry(20, 20);
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0xf5f5f5,
      opacity: 0.3,
      transparent: true,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.5;
    ground.receiveShadow = true;
    scene.add(ground);

    // Build floors
    buildFloors(scene, buildingData.floors);

    // Add navigation route if in navigation mode
    if (isNavigating && processedRoute) {
      buildNavigationRoute(scene, processedRoute);
    }

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      controls.update();

      // Animate navigation elements
      if (isNavigating) {
        animationTimeRef.current += 0.02;
        animateNavigationElements(scene);
      }

      renderer.render(scene, camera);
    };
    animate();

    setLoading(false);

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
      renderer.dispose();
      routeObjectsRef.current = [];
    };
  }, [buildingData, isNavigating, processedRoute]);

  // Animate navigation elements (pulsing markers, moving elevator)
  const animateNavigationElements = (scene) => {
    const time = animationTimeRef.current;

    scene.traverse((object) => {
      if (object.userData.isPulsingMarker) {
        const scale = 1 + Math.sin(time * 3) * 0.2;
        object.scale.setScalar(scale);
      }
      if (object.userData.isPulsingRing) {
        const scale = 1 + Math.sin(time * 2) * 0.3;
        object.scale.setScalar(scale);
        object.material.opacity = 0.5 + Math.sin(time * 2) * 0.2;
      }
      if (object.userData.isElevatorCabin) {
        // Animate elevator moving up and down
        const parent = object.parent;
        if (parent && parent.userData.elevatorHeight) {
          const height = parent.userData.elevatorHeight;
          object.position.y = height / 2 + Math.sin(time) * (height / 3);
        }
      }
      if (object.userData.isRoutePath) {
        // Animate dash offset for route lines
        if (object.material.dashOffset !== undefined) {
          object.material.dashOffset -= 0.02;
        }
      }
    });
  };

  // Build floor planes and nodes
  const buildFloors = (scene, floors) => {
    const textureLoader = new THREE.TextureLoader();
    nodeMeshesRef.current.clear();

    // Get route node IDs for filtering in navigation mode
    const routeNodeIds = new Set();
    if (isNavigating && navigationRoute) {
      navigationRoute.forEach(step => {
        if (step.node_id) routeNodeIds.add(step.node_id);
      });
    }

    floors.forEach((floor) => {
      const yPosition = floor.floor_number * floorSpacing;
      const aspectRatio = floor.image_width && floor.image_height
        ? floor.image_width / floor.image_height
        : 1;
      const planeHeight = planeWidth / aspectRatio;

      // Floor plane group
      const floorGroup = new THREE.Group();
      floorGroup.position.y = yPosition;
      floorGroup.userData = { floor };

      // Floor plane
      const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
      const planeMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: isNavigating ? 0.7 : 0.85,
      });

      // Load texture
      if (floor.image_url) {
        const imageUrl = floor.image_url.startsWith('http')
          ? floor.image_url
          : `${API_BASE_URL}${floor.image_url}`;

        textureLoader.load(
          imageUrl,
          (texture) => {
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            planeMaterial.map = texture;
            planeMaterial.needsUpdate = true;
          },
          undefined,
          (error) => console.error('Failed to load texture:', error)
        );
      }

      const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
      planeMesh.rotation.x = -Math.PI / 2;
      planeMesh.receiveShadow = true;
      floorGroup.add(planeMesh);

      // Add nodes (hidden in navigation mode, except special nodes)
      if (!isNavigating) {
        floor.nodes?.forEach((node) => {
          const xRatio = (node.x_position || 0) / (floor.image_width || 1);
          const yRatio = (node.y_position || 0) / (floor.image_height || 1);

          const x = (xRatio - 0.5) * planeWidth;
          const z = (yRatio - 0.5) * planeHeight;

          // Node sphere
          const nodeGeometry = new THREE.SphereGeometry(0.12, 16, 16);
          const nodeMaterial = new THREE.MeshStandardMaterial({
            color: getNodeColor(node.node_type),
            emissive: getNodeColor(node.node_type),
            emissiveIntensity: 0.1,
          });
          const nodeMesh = new THREE.Mesh(nodeGeometry, nodeMaterial);
          nodeMesh.position.set(x, 0.15, z);
          nodeMesh.userData = { node, floor };
          floorGroup.add(nodeMesh);

          // Store reference for interaction
          nodeMeshesRef.current.set(node.id, { mesh: nodeMesh, node, floor });

          // Vertical indicator for stairs/elevators
          if (node.node_type === 'stairs' || node.node_type === 'elevator') {
            const lineGeometry = new THREE.BufferGeometry().setFromPoints([
              new THREE.Vector3(x, 0.15, z),
              new THREE.Vector3(x, 0.65, z),
            ]);
            const lineMaterial = new THREE.LineDashedMaterial({
              color: getNodeColor(node.node_type),
              dashSize: 0.1,
              gapSize: 0.05,
            });
            const line = new THREE.Line(lineGeometry, lineMaterial);
            line.computeLineDistances();
            floorGroup.add(line);
          }
        });

        // Add edges (paths) - only in non-navigation mode
        floor.edges?.forEach((edge) => {
          const fromNode = floor.nodes?.find(n => n.id === edge.from_node_id);
          const toNode = floor.nodes?.find(n => n.id === edge.to_node_id);

          if (fromNode && toNode) {
            const fromXRatio = (fromNode.x_position || 0) / (floor.image_width || 1);
            const fromYRatio = (fromNode.y_position || 0) / (floor.image_height || 1);
            const toXRatio = (toNode.x_position || 0) / (floor.image_width || 1);
            const toYRatio = (toNode.y_position || 0) / (floor.image_height || 1);

            const fromX = (fromXRatio - 0.5) * planeWidth;
            const fromZ = (fromYRatio - 0.5) * planeHeight;
            const toX = (toXRatio - 0.5) * planeWidth;
            const toZ = (toYRatio - 0.5) * planeHeight;

            const edgeGeometry = new THREE.BufferGeometry().setFromPoints([
              new THREE.Vector3(fromX, 0.05, fromZ),
              new THREE.Vector3(toX, 0.05, toZ),
            ]);
            const edgeMaterial = new THREE.LineBasicMaterial({
              color: 0x607D8B,
              opacity: 0.6,
              transparent: true,
            });
            const edgeLine = new THREE.Line(edgeGeometry, edgeMaterial);
            floorGroup.add(edgeLine);
          }
        });
      }

      scene.add(floorGroup);
    });
  };

  // Build navigation route visualization
  const buildNavigationRoute = (scene, route) => {
    if (!route || !route.segments) return;

    const { segments, startStep, endStep } = route;

    // Clear previous route objects
    routeObjectsRef.current.forEach(obj => scene.remove(obj));
    routeObjectsRef.current = [];

    // Process each segment
    segments.forEach((segment, segmentIndex) => {
      const floor = segment.floor;
      const yPosition = floor.floor_number * floorSpacing;
      const aspectRatio = floor.image_width && floor.image_height
        ? floor.image_width / floor.image_height
        : 1;
      const planeHeight = planeWidth / aspectRatio;

      // Convert steps to 3D points
      const points = segment.steps.map(step => {
        const xRatio = (step.x_position || 0) / (floor.image_width || 1);
        const yRatio = (step.y_position || 0) / (floor.image_height || 1);
        const x = (xRatio - 0.5) * planeWidth;
        const z = (yRatio - 0.5) * planeHeight;
        return new THREE.Vector3(x, yPosition + 0.1, z);
      });

      // Create route tube for this segment
      if (points.length >= 2) {
        const routeTube = createRouteTube(points, ROUTE_COLOR, 0.05);
        if (routeTube) {
          scene.add(routeTube);
          routeObjectsRef.current.push(routeTube);
        }

        // Also add animated dashed line above
        const elevatedPoints = points.map(p => new THREE.Vector3(p.x, p.y + 0.05, p.z));
        const dashedLine = createAnimatedRouteLine(elevatedPoints, ROUTE_GLOW_COLOR);
        if (dashedLine) {
          dashedLine.userData.isRoutePath = true;
          scene.add(dashedLine);
          routeObjectsRef.current.push(dashedLine);
        }
      }

      // Check if this segment ends with vertical transport
      const lastStep = segment.steps[segment.steps.length - 1];
      const nextSegment = segments[segmentIndex + 1];

      if (nextSegment && (lastStep.node_type === 'stairs' || lastStep.node_type === 'elevator')) {
        const nextFloor = nextSegment.floor;
        const fromY = yPosition;
        const toY = nextFloor.floor_number * floorSpacing;

        const xRatio = (lastStep.x_position || 0) / (floor.image_width || 1);
        const yRatio = (lastStep.y_position || 0) / (floor.image_height || 1);
        const x = (xRatio - 0.5) * planeWidth;
        const z = (yRatio - 0.5) * planeHeight;

        // Create vertical transition line (straight line up/down)
        const verticalLine = createVerticalTransitionLine(
          lastStep.node_type,
          x,
          fromY,
          z,
          toY
        );
        scene.add(verticalLine);
        routeObjectsRef.current.push(verticalLine);
      }
    });

    // Add start marker
    if (startStep) {
      const startFloor = buildingData.floors.find(f => f.id === startStep.floor_plan_id);
      if (startFloor) {
        const aspectRatio = startFloor.image_width && startFloor.image_height
          ? startFloor.image_width / startFloor.image_height
          : 1;
        const planeHeight = planeWidth / aspectRatio;

        const xRatio = (startStep.x_position || 0) / (startFloor.image_width || 1);
        const yRatio = (startStep.y_position || 0) / (startFloor.image_height || 1);
        const x = (xRatio - 0.5) * planeWidth;
        const z = (yRatio - 0.5) * planeHeight;
        const y = startFloor.floor_number * floorSpacing;

        const startMarker = createStartMarker(x, y, z);
        scene.add(startMarker);
        routeObjectsRef.current.push(startMarker);
      }
    }

    // Add destination marker
    if (endStep) {
      const endFloor = buildingData.floors.find(f => f.id === endStep.floor_plan_id);
      if (endFloor) {
        const aspectRatio = endFloor.image_width && endFloor.image_height
          ? endFloor.image_width / endFloor.image_height
          : 1;
        const planeHeight = planeWidth / aspectRatio;

        const xRatio = (endStep.x_position || 0) / (endFloor.image_width || 1);
        const yRatio = (endStep.y_position || 0) / (endFloor.image_height || 1);
        const x = (xRatio - 0.5) * planeWidth;
        const z = (yRatio - 0.5) * planeHeight;
        const y = endFloor.floor_number * floorSpacing;

        const destMarker = createDestinationMarker(x, y, z);
        scene.add(destMarker);
        routeObjectsRef.current.push(destMarker);
      }
    }
  };

  // Handle mouse click for node selection (disabled in navigation mode)
  const handleClick = useCallback((event) => {
    if (isNavigating) return; // Disable node selection in navigation mode
    if (!containerRef.current || !cameraRef.current || !sceneRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    // Get all node meshes
    const nodeMeshes = Array.from(nodeMeshesRef.current.values()).map(n => n.mesh);
    const intersects = raycasterRef.current.intersectObjects(nodeMeshes);

    if (intersects.length > 0) {
      const clickedMesh = intersects[0].object;
      const { node, floor } = clickedMesh.userData;
      if (node && floor) {
        onNodeSelect?.(node, floor);
      }
    }
  }, [onNodeSelect, isNavigating]);

  // Handle mouse move for hover effect (disabled in navigation mode)
  const handleMouseMove = useCallback((event) => {
    if (isNavigating) return;
    if (!containerRef.current || !cameraRef.current || !sceneRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    const nodeMeshes = Array.from(nodeMeshesRef.current.values()).map(n => n.mesh);
    const intersects = raycasterRef.current.intersectObjects(nodeMeshes);

    // Reset all nodes
    nodeMeshesRef.current.forEach(({ mesh, node }) => {
      const isSelected = selectedDestination?.node?.id === node.id;
      mesh.scale.setScalar(isSelected ? 1.5 : 1);
      mesh.material.emissiveIntensity = isSelected ? 0.5 : 0.1;
    });

    if (intersects.length > 0) {
      const hoveredMesh = intersects[0].object;
      hoveredMesh.scale.setScalar(1.3);
      hoveredMesh.material.emissiveIntensity = 0.3;
      setHoveredNode(hoveredMesh.userData);
      containerRef.current.style.cursor = 'pointer';
    } else {
      setHoveredNode(null);
      if (containerRef.current) {
        containerRef.current.style.cursor = 'default';
      }
    }
  }, [selectedDestination, isNavigating]);

  // Update selected node highlight
  useEffect(() => {
    if (!selectedDestination?.node?.id || isNavigating) return;

    nodeMeshesRef.current.forEach(({ mesh, node }) => {
      const isSelected = selectedDestination.node.id === node.id;
      mesh.scale.setScalar(isSelected ? 1.5 : 1);
      mesh.material.emissiveIntensity = isSelected ? 0.5 : 0.1;
    });
  }, [selectedDestination, isNavigating]);

  // Add event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('click', handleClick);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleClick, handleMouseMove]);

  const toggleFloorList = () => {
    setExpandedFloors(!expandedFloors);
  };

  // Camera controls
  const handleZoomIn = () => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.multiplyScalar(0.8);
      controlsRef.current.update();
    }
  };

  const handleZoomOut = () => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.multiplyScalar(1.2);
      controlsRef.current.update();
    }
  };

  const handleCenter = () => {
    if (cameraRef.current && controlsRef.current && buildingData?.floors) {
      const centerY = (buildingData.floors.length * floorSpacing) / 2;
      cameraRef.current.position.set(15, 15, 15);
      controlsRef.current.target.set(0, centerY, 0);
      controlsRef.current.update();
    }
  };

  if (!buildingData || !buildingData.floors?.length) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No building data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', position: 'relative' }}>
      {/* 3D Canvas Container */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          height: '100%',
          borderRadius: isMobile ? 0 : 1,
          overflow: 'hidden',
          position: 'relative',
          touchAction: 'none',
        }}
      >
        {loading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Camera controls */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: isMobile ? 80 : 16,
          left: 16,
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          gap: 0.5,
          p: 0.5,
        }}
        elevation={3}
      >
        <Tooltip title="Zoom In" placement={isMobile ? 'top' : 'right'}>
          <IconButton size={isMobile ? 'medium' : 'small'} onClick={handleZoomIn}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out" placement={isMobile ? 'top' : 'right'}>
          <IconButton size={isMobile ? 'medium' : 'small'} onClick={handleZoomOut}>
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset View" placement={isMobile ? 'top' : 'right'}>
          <IconButton size={isMobile ? 'medium' : 'small'} onClick={handleCenter}>
            <CenterIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Navigation mode indicator */}
      {isNavigating && (
        <Paper
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
          elevation={3}
        >
          <NavigationIcon />
          <Typography variant="body2" fontWeight="bold">
            Navigation Mode
          </Typography>
        </Paper>
      )}

      {/* Hovered node tooltip (not in navigation mode) */}
      {!isNavigating && hoveredNode && (
        <Paper
          sx={{
            position: 'absolute',
            top: isMobile ? 70 : 16,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 2,
            py: 1,
            bgcolor: 'rgba(0,0,0,0.8)',
            maxWidth: isMobile ? '80%' : 'auto',
          }}
          elevation={3}
        >
          <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
            {hoveredNode.node?.name || `${hoveredNode.node?.node_type} ${hoveredNode.node?.id}`}
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.400', display: 'block', textAlign: 'center' }}>
            {hoveredNode.floor?.floor_name} - {isMobile ? 'Tap' : 'Click'} to select
          </Typography>
        </Paper>
      )}

      {/* Floor list toggle button for mobile */}
      {isMobile && !isNavigating && (
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'background.paper' },
          }}
          onClick={() => setShowFloorPanel(!showFloorPanel)}
        >
          <LayersIcon color={showFloorPanel ? 'primary' : 'inherit'} />
        </IconButton>
      )}

      {/* Side panel - Floor list (hidden in navigation mode) */}
      {!isNavigating && (showFloorPanel || !isMobile) && (
        <Paper
          sx={{
            position: 'absolute',
            top: isMobile ? 56 : 16,
            right: isMobile ? 8 : 16,
            width: isMobile ? 220 : 280,
            maxHeight: isMobile ? 'calc(100% - 150px)' : 'calc(100% - 32px)',
            overflow: 'auto',
          }}
          elevation={3}
        >
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: 1,
              borderColor: 'divider',
              cursor: 'pointer',
            }}
            onClick={toggleFloorList}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LayersIcon color="primary" fontSize={isMobile ? 'small' : 'medium'} />
              <Typography variant={isMobile ? 'body2' : 'subtitle2'} fontWeight="bold">
                Floors ({buildingData.floors.length})
              </Typography>
            </Box>
            {expandedFloors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>

          <Collapse in={expandedFloors}>
            <List dense sx={{ py: 0 }}>
              {buildingData.floors
                .slice()
                .sort((a, b) => b.floor_number - a.floor_number)
                .map((floor) => {
                  const stairsCount = floor.nodes?.filter(n => n.node_type === 'stairs').length || 0;
                  const elevatorCount = floor.nodes?.filter(n => n.node_type === 'elevator').length || 0;

                  return (
                    <ListItemButton
                      key={floor.id}
                      selected={selectedDestination?.floorPlan?.id === floor.id}
                      sx={{ py: isMobile ? 1 : 0.5 }}
                    >
                      <ListItemText
                        primary={floor.floor_name}
                        secondary={isMobile ? `${floor.nodes?.length || 0} nodes` : `Floor ${floor.floor_number} - ${floor.nodes?.length || 0} nodes`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {!isMobile && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {stairsCount > 0 && (
                            <Tooltip title={`${stairsCount} stairs`}>
                              <Chip
                                icon={<StairsIcon sx={{ fontSize: 14 }} />}
                                label={stairsCount}
                                size="small"
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            </Tooltip>
                          )}
                          {elevatorCount > 0 && (
                            <Tooltip title={`${elevatorCount} elevators`}>
                              <Chip
                                icon={<ElevatorIcon sx={{ fontSize: 14 }} />}
                                label={elevatorCount}
                                size="small"
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </ListItemButton>
                  );
                })}
            </List>
          </Collapse>

          {/* Instructions - hidden on mobile */}
          {!isMobile && (
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">
                Click and drag to rotate. Scroll to zoom. Click on a node to select destination.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Navigation legend (only in navigation mode) */}
      {isNavigating && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: isMobile ? 80 : 16,
            right: 16,
            p: 1.5,
            minWidth: 150,
          }}
          elevation={3}
        >
          <Typography variant="caption" fontWeight="bold" gutterBottom display="block">
            Legend
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 4, bgcolor: '#00E676', borderRadius: 1 }} />
              <Typography variant="caption">Walking path</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StairsIcon sx={{ fontSize: 16, color: '#AB47BC' }} />
              <Typography variant="caption">Stairs</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ElevatorIcon sx={{ fontSize: 16, color: '#26A69A' }} />
              <Typography variant="caption">Elevator</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, bgcolor: '#FF5722', borderRadius: '50%' }} />
              <Typography variant="caption">Destination</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Selected destination info (not in navigation mode) */}
      {!isNavigating && selectedDestination && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: isMobile ? 80 : 16,
            left: isMobile ? '50%' : 80,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            p: isMobile ? 1.5 : 2,
            maxWidth: isMobile ? '90%' : 300,
            width: isMobile ? 'auto' : 'auto',
          }}
          elevation={3}
        >
          <Typography variant={isMobile ? 'body2' : 'subtitle2'} fontWeight="bold" gutterBottom>
            Selected: {selectedDestination.node?.name || `Node ${selectedDestination.node?.id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedDestination.floorPlan?.floor_name} (Floor {selectedDestination.floorPlan?.floor_number})
          </Typography>
        </Paper>
      )}

      {/* Mobile instruction hint (not in navigation mode) */}
      {isMobile && !isNavigating && !hoveredNode && !selectedDestination && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 2,
            py: 1,
            bgcolor: 'rgba(0,0,0,0.6)',
          }}
          elevation={3}
        >
          <Typography variant="caption" sx={{ color: 'white' }}>
            Drag to rotate • Pinch to zoom • Tap nodes to select
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default Building3DViewer;
